// Lib
import { useCallback, useLayoutEffect, useRef } from 'react';
import type Cropper from 'cropperjs';

// Hooks
import { useImageRotation } from '../../../content/hooks/useImageRotation';
import {
    IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY,
    useImageModalEditorImageLoader,
} from '../../../content/hooks/useImageModalEditorImageLoader';

// Utils
import { adjustCropperForRotation, reflowCropperAfterTransforms } from '../../../content/utils/cropperUtils';
import {
    isImageModalEditorSourceModeSwap,
    isImageModalEditorVariantSwap,
    type ImageModalEditorSourceIdentity,
} from '../../../content/utils/imageModalEditorSourceUtils';

// Types
import type { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';

type UseImageModalV2EditorCropperOptions = {
    element: ImMNElement;
    elementId: string;
    imageType: string;
    largestUneditedImageUrl: string | undefined;
    shouldUseOriginalEditorSource: boolean;
    // While frozen the editor is intentionally holding a snapshot for an in-flight save, so identity
    // changes coming from the (now-changing) element prop must not reset the session.
    isFrozen: boolean;
    editorIsReady: boolean;
    // Transform session wiring (see useImageModalV2EditorTransformSession).
    initialRotation: number;
    currentRotation: number;
    currentScaleX: number;
    currentScaleY: number;
    setCurrentRotation: (val: number) => void;
    setCurrentScaleX: (val: number) => void;
    setCurrentScaleY: (val: number) => void;
    resetSessionToInitial: () => void;
    markEditorChanged: () => void;
    // Loader wiring: the load key/URL the save freeze has resolved as active, plus the alternate-source
    // prefetch so the crop toggle swaps without a flash.
    activeEditorImageLoadKey: string;
    activeLargestUneditedImageUrl: string | undefined;
    prefetchEditorSource: { sourceUrl: string; imageType: string } | null;
    onImageDataUrlLoad: (dataUrl: string) => void;
    handleEditorReadyChange: (ready: boolean) => void;
    consumePendingSwapReflow: () => boolean;
    // The pinned footprint the editor box is currently laid out at (see resolvePinnedEditorFootprint).
    displayModalWidth: number;
    displayModalHeight: number;
};

/**
 * Owns the V2 editor's Cropper instance: the element/canvas refs, the image loader that (re)builds the
 * cropper as sources swap, rotate/flip commands, and the layout effects that keep the cropper's geometry
 * in step with source-identity changes, rotations, and footprint-pin releases.
 */
export const useImageModalV2EditorCropper = ({
    element,
    elementId,
    imageType,
    largestUneditedImageUrl,
    shouldUseOriginalEditorSource,
    isFrozen,
    editorIsReady,
    initialRotation,
    currentRotation,
    currentScaleX,
    currentScaleY,
    setCurrentRotation,
    setCurrentScaleX,
    setCurrentScaleY,
    resetSessionToInitial,
    markEditorChanged,
    activeEditorImageLoadKey,
    activeLargestUneditedImageUrl,
    prefetchEditorSource,
    onImageDataUrlLoad,
    handleEditorReadyChange,
    consumePendingSwapReflow,
    displayModalWidth,
    displayModalHeight,
}: UseImageModalV2EditorCropperOptions) => {
    const imageRef = useRef<HTMLImageElement | null>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const isDirtyRef = useRef(false);
    const initialCropDataRef = useRef<Cropper.CropBoxData | null>(null);
    // Tracks the image currently loaded so a background-removal source swap (same element, different source
    // URL) can be told apart from a genuinely new image.
    const previousImageIdentityRef = useRef<ImageModalEditorSourceIdentity | null>(null);
    // Live rotate/flip/crop snapshotted before a background-removal source swap, re-applied once the swapped-in
    // image loads so the user's in-progress edits survive the reload.
    const pendingCropperDataRef = useRef<Cropper.Data | null>(null);

    const getLiveCropperData = useCallback(() => cropperRef.current?.getData(), []);
    const setPendingCropperData = useCallback((data: Cropper.Data) => {
        pendingCropperDataRef.current = data;
    }, []);

    const { handleRotateImage, handleFlipImageHorizontally, handleFlipImageVertically } = useImageRotation({
        cropperRef,
        isDirtyRef,
        initialCropDataRef,
        currentRotation,
        initialRotation,
        currentScaleX,
        currentScaleY,
        setCurrentRotation,
        setCurrentScaleX,
        setCurrentScaleY,
        // V2 path-bakes the drawing through the live session transform, so it never hides on rotate.
        preserveDrawingOnRotate: true,
        mirrorCropSelectionOnFlip: true,
    });

    useLayoutEffect(() => {
        if (isFrozen) return;

        const nextIdentity = {
            elementId,
            imageType,
            url: largestUneditedImageUrl,
            usesOriginalSource: shouldUseOriginalEditorSource,
        };
        const isVariantSwap = isImageModalEditorVariantSwap(previousImageIdentityRef.current, nextIdentity);
        const isSourceModeSwap = isImageModalEditorSourceModeSwap(previousImageIdentityRef.current, nextIdentity);
        previousImageIdentityRef.current = nextIdentity;

        if (isVariantSwap) {
            // Snapshot the live cropper state before the reload tears the cropper down. It's re-applied in
            // loadCanvasIntoCropper's ready callback once the swapped-in image is loaded.
            pendingCropperDataRef.current = cropperRef.current?.getData() ?? null;
            return;
        }

        if (isSourceModeSwap) {
            initialCropDataRef.current = null;
            return;
        }

        // A genuinely new image: rewind the whole session to its starting point.
        pendingCropperDataRef.current = null;
        resetSessionToInitial();
        handleEditorReadyChange(false);
        isDirtyRef.current = false;
        initialCropDataRef.current = null;
    }, [
        elementId,
        handleEditorReadyChange,
        imageType,
        isFrozen,
        largestUneditedImageUrl,
        resetSessionToInitial,
        shouldUseOriginalEditorSource,
    ]);

    useImageModalEditorImageLoader({
        imageRef,
        cropperRef,
        isDirtyRef,
        element,
        imageType,
        loadKey: activeEditorImageLoadKey,
        loadStrategy: IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.KEEP_PREVIOUS_IMAGE_UNTIL_READY,
        sourceUrl: activeLargestUneditedImageUrl,
        prefetchSource: prefetchEditorSource,
        onEditorReadyChange: handleEditorReadyChange,
        onImageDataUrlLoad,
        applyStoredCropData: shouldUseOriginalEditorSource,
        pendingCropperDataRef,
        onCropChange: markEditorChanged,
        recenterAfterTransforms: false,
        // The rest of the editor already holds a save-time snapshot; without this the loader is the one
        // consumer still reacting mid-save, and a rebuild there loses the session rotation for its duration.
        isPaused: isFrozen,
    });

    useLayoutEffect(() => {
        const cropper = cropperRef.current;
        if (isFrozen || !editorIsReady || !cropper) return;
        if (!initialCropDataRef.current) return;

        adjustCropperForRotation(cropper, displayModalWidth, initialCropDataRef.current);
    }, [currentRotation, displayModalWidth, editorIsReady, isFrozen]);

    // Releasing the crop-tool footprint pin resizes the editor box under an already-rendered Cropper,
    // so rebuild its geometry (preserving the applied selection) once the destination box has laid out.
    useLayoutEffect(() => {
        const cropper = cropperRef.current;
        if (!cropper || !consumePendingSwapReflow()) return;

        reflowCropperAfterTransforms(cropper, cropper.getData());
    }, [consumePendingSwapReflow, displayModalHeight, displayModalWidth]);

    return {
        imageRef,
        cropperRef,
        isDirtyRef,
        getLiveCropperData,
        setPendingCropperData,
        handleRotateImage,
        handleFlipImageHorizontally,
        handleFlipImageVertically,
    };
};
